import { useState } from "react";
import { useIsFetching, useQuery, useQueryClient } from "@tanstack/react-query";
import { addDays, startOfDay } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  ChevronsUpDown,
  RefreshCw,
  Calendar,
  FileDown,
  Package,
} from "lucide-react";
import { productQueries } from "@/api/product";
import { productAnalyticsQueries } from "@/api/product-analytics";
import { useToast } from "@/hooks/use-toast";
import { DateRange } from "@/lib/date";
import { AllProductsAnalytics } from "@/components/admin/product-analytics/AllProductsAnalytics";
import { DetailedProductAnalytics } from "@/components/admin/product-analytics/DetailedProductAnalytics";

// Time Preset Ranges
const TIME_PRESETS = [
  { label: "Last 7 Days", days: 7 },
  { label: "Last 30 Days", days: 30 },
  { label: "Last 3 Months", days: 90 },
  { label: "Last 6 Months", days: 180 },
  { label: "Year to Date", days: 365 },
];

export function ProductAnalyticsPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // -------------- State --------------
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null,
  );
  const [dateRange, setDateRange] = useState<DateRange>({
    from: addDays(new Date(), -30),
    to: new Date(),
  });
  const [selectedTimePreset, setSelectedTimePreset] = useState("30");

  // Control whether the product dropdown popover is open
  const [productOpen, setProductOpen] = useState(false);

  // -------------- Queries --------------
  const { data: allProducts = [] } = useQuery({
    ...productQueries.products(),
  });

  // If no product is selected => fetch aggregator for ALL products
  const { data: allProductsSummary } = useQuery({
    ...productAnalyticsQueries.summary(
      dateRange.from?.toISOString(),
      dateRange.to?.toISOString(),
    ),
    enabled: !selectedProductId,
  });

  // If product is selected => single-product aggregator
  const { data: singleProductAnalytics } = useQuery({
    ...productAnalyticsQueries.product(
      selectedProductId ?? "",
      dateRange.from?.toISOString(),
      dateRange.to?.toISOString(),
    ),
    enabled: !!selectedProductId,
  });

  // Derived
  const selectedProduct = allProducts.find(
    (p) => p.product_sku === selectedProductId,
  );

  // -------------- Handlers --------------
  const handleProductSelect = (pid: string) => {
    if (pid === "__ALL__") {
      setSelectedProductId(null);
    } else {
      setSelectedProductId(pid);
    }
    setProductOpen(false);
  };

  const handleTimePresetSelect = (days: number) => {
    setSelectedTimePreset(days.toString());
    setDateRange({
      from: addDays(new Date(), -days),
      to: new Date(),
    });
  };

  const handleRefreshData = () => {
    toast({ title: "Refreshing data", description: "Please wait..." });
    if (selectedProductId) {
      queryClient.invalidateQueries(
        productAnalyticsQueries.product(selectedProductId),
      );
    } else {
      queryClient.invalidateQueries(productAnalyticsQueries.all());
    }
  };

  const handleExportData = () => {
    // If single product
    if (selectedProductId && singleProductAnalytics) {
      const csvRows = [
        ["Date", "Credits", "API Calls", "Avg Credits/Call"],
        ...singleProductAnalytics.daily_usage.map((day) => {
          const calls = day.calls ?? 0;
          const creds = day.credits ?? 0;
          return [
            day.date,
            creds.toString(),
            calls.toString(),
            calls > 0 ? (creds / calls).toFixed(2) : "0.00",
          ];
        }),
      ];
      const csvString = csvRows.map((r) => r.join(",")).join("\n");
      const blob = new Blob([csvString], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `product-analytics-${selectedProductId}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } else if (allProductsSummary) {
      // Export “All Products” summary
      const rows = [
        ["Product", "Total Credits", "Total Calls", "Avg Credits/Call"],
      ];
      Object.entries(allProductsSummary.products ?? {}).forEach(
        ([sku, usage]) => {
          const c = usage.total_credits ?? 0;
          const calls = usage.total_calls ?? 0;
          rows.push([
            sku,
            c.toString(),
            calls.toString(),
            calls > 0 ? (c / calls).toFixed(2) : "0.00",
          ]);
        },
      );
      const csvString = rows.map((r) => r.join(",")).join("\n");
      const blob = new Blob([csvString], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `all-products-analytics.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  };

  const isLoadingData = useIsFetching(productAnalyticsQueries.all()) > 0;

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <CardHeader className="px-0 pt-0">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-2xl font-semibold text-primary">
                Product Analytics Dashboard
              </CardTitle>
              <CardDescription>
                {selectedProductId
                  ? "Detailed analytics for the selected product"
                  : "Aggregate analytics across all products"}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={handleRefreshData}
                disabled={isLoadingData}
              >
                <RefreshCw
                  className={`h-4 w-4 ${isLoadingData ? "animate-spin" : ""}`}
                />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={handleExportData}
                disabled={isLoadingData}
              >
                <FileDown className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="px-0">
          {/* Product Selection & Date Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {/* Product Selection */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Package className="h-4 w-4" />
                {selectedProductId
                  ? "Selected Product"
                  : "Optional: Pick Product"}
              </Label>
              <Popover open={productOpen} onOpenChange={setProductOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-between rounded-lg"
                    role="combobox"
                    aria-expanded={productOpen}
                  >
                    {selectedProductId && selectedProduct ? (
                      <div className="flex items-center gap-2">
                        <span className="truncate">{selectedProduct.name}</span>
                        <Badge variant="secondary">
                          {selectedProduct.product_family}
                        </Badge>
                      </div>
                    ) : (
                      "All Products"
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0">
                  <Command>
                    <CommandInput placeholder="Search products..." />
                    <CommandList>
                      <CommandEmpty>No products found.</CommandEmpty>
                      <CommandGroup>
                        {/* "All Products" */}
                        <CommandItem
                          key="__ALL__"
                          onSelect={() => handleProductSelect("__ALL__")}
                          value="__ALL__"
                        >
                          <div className="flex items-center justify-between w-full">
                            <span>All Products</span>
                            <Badge variant="outline">Aggregate</Badge>
                          </div>
                        </CommandItem>
                        {/* Actual products */}
                        {allProducts.map((p) => (
                          <CommandItem
                            key={p.product_sku}
                            onSelect={() => handleProductSelect(p.product_sku)}
                            value={p.product_sku}
                          >
                            <div className="flex items-center justify-between w-full">
                              <span>{p.name}</span>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline">
                                  {p.product_family}
                                </Badge>
                                <Badge variant="secondary">
                                  {p.credit_cost} credits
                                </Badge>
                              </div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Time Presets */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Time Period
              </Label>
              <Select
                value={selectedTimePreset}
                onValueChange={(v) => handleTimePresetSelect(Number(v))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select time period" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_PRESETS.map((p) => (
                    <SelectItem key={p.days} value={p.days.toString()}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Custom Date Range */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Custom Range
              </Label>
              <DateRangePicker
                value={dateRange}
                onChange={(range) => {
                  if (range?.from && range?.to) {
                    setDateRange({
                      from: startOfDay(range.from),
                      to: startOfDay(range.to),
                    });
                  }
                }}
              />
            </div>
          </div>

          {selectedProductId ? (
            <DetailedProductAnalytics
              key={selectedProductId}
              productId={selectedProductId}
              dateRange={dateRange}
            />
          ) : (
            <AllProductsAnalytics dateRange={dateRange} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
